<template>
    <div class="video" ref="videoContainer">
        <div v-if="channelNumber" class="video__channel">{{ channelName }}</div>
        <div class="video__wrapper">
            <div :class="['video__video-player', {'video__video-player--fullscreen': isFullScreenOpen}]">
                <div ref="videoSubtitles" class="video__subtitles"></div>
                <video
                    @loadeddata="isLoaded"
                    ref="videoPlayer"
                    :id="`videoPlayer${channelNumber}`"
                    class="video__video"
                    preload="none"
                    :src="this.url"
                    @timeupdate="handleVideoTimeUpdate"
                    @ended="handleVideoEnded"
                    @loadedmetadata="setVideoDuration"
                    @resize="resizeSubtitles"
                ></video>
                <video-controls
                    :is-live-stream="isLiveStream"
                    :play-time="playTime"
                    :video-length="videoLength"
                    :video-playing="videoPlaying"
                    :is-full-screen="isFullScreenOpen"
                    :has-subtitles="tracks.length > 0"
                    :can-download-video="canDownloadVideo"
                    :handle-fullscreen="handleFullScreen"
                    :handle-video-download="handleVideoDownload"
                    :handle-video-progress="handleVideoProgress"
                    :handle-video-play="handleVideoPlay"
                    :handle-ass-visibility="handleAssVisibility"
                ></video-controls>
            </div>
        </div>
    </div>
</template>

<script>
import dashjs from 'dashjs'
import { mapGetters } from 'vuex'
import ASS from 'assjs'
import VideoControls from '@/components/atoms/VideoControls'

export default {
    name: 'VideoMPEG',
    components: {
        VideoControls
    },
    data () {
        return {
            videoPlaying: false,
            playTime: 0,
            videoLength: 0,
            player: null,
            isFullScreenOpen: false,
            isAssVisible: true,
            assRefs: [],
            isMounted: true
        }
    },
    props: {
        isGrid: {
            type: Boolean,
            required: false,
            default: false
        },
        channelNumber: {
            type: Number,
            required: false,
            default: null
        },
        url: {
            type: String,
            required: true
        },
        tracks: {
            type: Array,
            required: false,
            default: () => []
        },
        dateCaptured: {
            type: String,
            required: false,
            default: ''
        },
        isLiveStream: {
            type: Boolean,
            required: false,
            default: false
        },
        canDownloadVideo: {
            type: Boolean,
            required: false,
            default: true
        },
        isVideoLoaded: {
            type: Boolean,
            required: false
        }
    },
    computed: {
        ...mapGetters({
            getChannels: 'dictionaryModule/getChannels'
        }),
        channelName () {
            return this.getChannels.find(channel => channel.id === this.channelNumber).key
        }
    },
    mounted () {
        if (this.$refs.videoPlayer) {
            this.createVideoStream()

            if (this.tracks && 0 < this.tracks.length) {
                this.renderSubtitles()
            }
        }
    },
    watch: {
        isFullScreenOpen (value) {
            this.$nextTick(() => {
                this.assRefs.forEach(ass => {
                    setTimeout(() => {
                        ass.resize()
                    }, 500)
                })
            })
        },
        isGrid () {
            this.$nextTick(() => {
                this.assRefs.forEach(ass => {
                    ass.resize()
                })
            })
        },
        isVideoLoaded: {
            handler (val) {
                if (val) {
                    this.handleVideoPlay()
                }
            }
        }
    },
    methods: {
        isLoaded () {
            this.$emit('numberLoadedVideo')
        },
        createVideoStream () {
            const player = dashjs.MediaPlayer().create()
            player.setFragmentLoaderRetryInterval(500)
            player.setManifestLoaderRetryInterval(500)
            player.initialize(this.$refs.videoPlayer, this.url, false)
            player.setScheduleWhilePaused(true)
            player.setJumpGaps(true)
            player.setLiveDelay(5)
            player.setLowLatencyEnabled(false)
            // player.setLowLatencyEnabled(true)
            // player.setLowLatencyMinDrift(0.05)
            // player.setLowLatencyMaxDriftBeforeSeeking(3)

            // player.play()
        },
        resizeSubtitles () {
            this.assRefs.forEach(ass => {
                setTimeout(() => {
                    ass.resize()
                }, 500)
            })
        },
        renderSubtitles () {
            setTimeout(() => {
                this.tracks.forEach(track => {
                    fetch(track.uri)
                        .then(res => res.text())
                        .then(text => {
                            const ass = new ASS(text, this.$refs.videoPlayer, {
                                container: this.$refs.videoSubtitles
                            })
                            this.assRefs.push(ass)
                        }).then(() => {
                            this.$refs.videoPlayer.addEventListener('resize', () => {
                                this.assRefs.forEach(ass => ass.resize())
                            })
                        })
                })
            }, 1000)
        },
        handleAssVisibility () {
            this.assRefs.forEach(ass => {
                if (this.isAssVisible) {
                    ass.hide()
                } else {
                    ass.show()
                }
            })
            this.isAssVisible = !this.isAssVisible
        },
        handleVideoPlay () {
            this.videoPlaying = true
            if (!this.$refs.videoPlayer) return

            if (this.$refs.videoPlayer.paused) {
                this.$refs.videoPlayer.play()
            } else if (this.$refs.videoPlayer.ended) {
                this.videoPlaying = true
                this.$refs.videoPlayer.play()
            } else {
                this.videoPlaying = false
                this.$refs.videoPlayer.pause()
            }
        },
        handleVideoProgress (value) {
            this.playTime = value
            this.$refs.videoPlayer.currentTime = value
        },
        handleVideoTimeUpdate (evt) {
            if (this.$refs.videoPlayer) {
                this.playTime = Math.floor(this.$refs.videoPlayer.currentTime)
            } else {
                this.playTime = Math.floor(evt.target.currentTime)
            }
        },
        handleVideoEnded () {
            this.playTime = 0
            this.videoPlaying = false
        },
        setVideoDuration (evt) {
            if (this.$refs.videoPlayer) {
                this.videoLength = Math.floor(this.$refs.videoPlayer.duration)
            } else {
                this.videoLength = Math.floor(evt.target.duration)
            }
        },
        isFullScreen () {
            return !!(
                document.fullscreen ||
                document.webkitIsFullScreen ||
                document.mozFullScreen ||
                document.msFullscreenElement ||
                document.fullscreenElement ||
                document.webkitDisplayingFullscreen
            )
        },
        handleFullScreen () {
            if (this.isFullScreen()) {
                if (document.exitFullscreen) document.exitFullscreen()
                else if (document.mozCancelFullScreen) document.mozCancelFullScreen()
                else if (document.webkitCancelFullScreen) document.webkitCancelFullScreen()
                else if (document.msExitFullscreen) document.msExitFullscreen()
                else if (document.webkitExitFullscreen) document.webkitExitFullscreen()
                this.isFullScreenOpen = false
                return
            } else if (this.$refs.videoContainer.requestFullscreen) {
                this.$refs.videoContainer.requestFullscreen()
            } else if (this.$refs.videoContainer.mozRequestFullscreen) {
                this.$refs.videoContainer.mozRequestFullscreen()
            } else if (this.$refs.videoContainer.webkitRequestFullScreen) {
                this.$refs.videoContainer.webkitRequestFullScreen()
            } else if (this.$refs.videoContainer.webkitSupportsFullscreen) {
                this.$refs.videoContainer.webkitEnterFullscreen()
            } else if (this.$refs.videoContainer.msRequestFullscreen) {
                this.$refs.videoContainer.msRequestFullscreen()
            }
            this.isFullScreenOpen = true
        },
        handleVideoDownload () {
            const a = document.createElement('a')
            a.href = this.url
            a.download = this.dateCaptured
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
        }
    }
}
</script>

<style scoped lang="scss">
.video {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    &__channel {
        position: absolute;
        top: 0px;
        right: 0px;
        font-size: 14px;
        color: $white;
        z-index: 99;
    }

    &__wrapper {
        flex: 1;
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
        height: 100%;
        width: 100%;
    }

    &__video-player {
        //position: relative;
        //display: inline-flex;
        //justify-content: center;
        //align-items: center;
        //height: auto;
        //width: auto;
        object-fit: contain;
        width: 100%;
        height: 100%;

        &--fullscreen {

            .video__video {

            }
        }
    }

    &__subtitles {
        position: absolute;
        top: 0;
        left: 0;
        width: 100% !important;
        height: 100% !important;
    }

    &__video {
        width: 100%;
        height: 100%;
        max-height: 100%;
    }
}
</style>
